import { buildDeploymentGroups, Metadata, Dep } from '../../../../api/granulardeploy/src/deployGroups'
import { ArtifactoryImageResult } from '../../../../api/granulardeploy/src/validationsBE';
import { ServiceDep } from '../hooks/useFinalDeps';
import { fetchApi, withCluster } from '@app/hooks/api';

export interface BulkCheckItem {
  appName: string;
  repoName: string;
  branch: string;
}

export interface BulkCheckResult {
  application: string;
  branch: string;
  exists?: boolean;
  behindInfo?: { application: string; branch: string; behind_by: number };
  images?: ArtifactoryImageResult | null;
}

export interface QuotaCheckResult {
  skipped: boolean;
  slots?: number; // deprecated, maintained for backward compatibility
  message?: string;
  error?: string;
  team?: string;
  runningCount?: number;
  runningNamespaces?: string[];
  namespaces?: string[]; // For cluster capacity errors
}

export interface BudgetInfoResult {
  available: boolean;
  team?: string;
  monthlyBudget?: number;
  currentSpend?: number;
  budgetRemaining?: number;
  budgetUtilizedPercent?: number;
  budgetExceeded?: boolean;
  /** True when the EOD budget is enforced server-side (creation is blocked when exceeded). */
  enforced?: boolean;
  formUrl?: string;
  message?: string;
  error?: string;
  stale?: boolean;
  metadata?: {
    lastUpdated?: number;
    refreshInProgress: boolean;
    cacheAgeMs?: number;
    refreshCount: number;
    consecutiveFailures: number;
    lastError?: string;
    lastRefreshDurationMs?: number;
    allocationWindowStart?: string;
    allocationWindowEnd?: string;
  };
}

export interface NamespaceCost {
  accumulated_month: number,
  daily: number,
}

export interface PredictionInfoResult {
  team: string;
  namespaces: Array<{
    name: string;
    costBefore: {
      totalDailyRate: number;
      cpuDailyRate: number;
      ramDailyRate: number;
      gpuDailyRate?: number;
      pvDailyRate?: number;
    };
    costBeforeMonthly?: {
      totalMonthlyRate: number;
      cpuMonthlyRate: number;
      ramMonthlyRate: number;
      gpuMonthlyRate?: number;
      pvMonthlyRate?: number;
    };
    costAfter: {
      totalDailyRate: number;
      cpuDailyRate: number;
      ramDailyRate: number;
    };
    costChange: {
      totalDailyRate: number;
      cpuDailyRate: number;
      ramDailyRate: number;
    };
    workloadCount: number;
    workloads?: Array<{
      controllerName: string;
      controllerKind: string;
      costAfter: {
        totalDailyRate: number;
        cpuDailyRate: number;
        ramDailyRate: number;
      };
    }>;
  }>;
  total: {
    costBefore: {
      totalDailyRate: number;
      cpuDailyRate: number;
      ramDailyRate: number;
      gpuDailyRate?: number;
      pvDailyRate?: number;
    };
    costBeforeMonthly?: {
      totalMonthlyRate: number;
      cpuMonthlyRate: number;
      ramMonthlyRate: number;
      gpuMonthlyRate?: number;
      pvMonthlyRate?: number;
    };
    costAfter: {
      totalDailyRate: number;
      cpuDailyRate: number;
      ramDailyRate: number;
    };
    costChange: {
      totalDailyRate: number;
      cpuDailyRate: number;
      ramDailyRate: number;
    };
  };
  lastUpdated: string;
  error?: string;
}

// POST /api/git/bulk-check
export async function apiBulkCheck(items: BulkCheckItem[], tokenGetter: () => Promise<string>): Promise<{ results: BulkCheckResult[]; errors?: string[] }> {
  const data = await fetchApi<{ results: BulkCheckResult[]; errorsCount?: number; errors?: string[] }>(
    '/api/git/bulk-check',
    tokenGetter,
    { method: 'POST', body: JSON.stringify({ items }) },
  );
  return { results: data.results || [], ...(data.errors?.length ? { errors: data.errors } : {}) };
}

export async function apiGitBranches(application: string, branch: string, tokenGetter: () => Promise<string>, medullaBranchName?: string): Promise<string[]> {
  const qs = new URLSearchParams({ application, branch });

  if (medullaBranchName && medullaBranchName !== 'master' && medullaBranchName !== branch) {
    qs.set('medullaBranchName', medullaBranchName);
  }

  const data = await fetchApi<{ branches: string[] }>(
    `/api/git/branches?${qs}`,
    tokenGetter,
  );
  return data.branches || [];
}

export async function apiValidateDeployBranch(branch: string, tokenGetter: () => Promise<string>) {
  return fetchApi<{ exists: boolean; behindBy?: number }>(
    `/api/git/validate-deploy-branch?branch=${encodeURIComponent(branch)}`,
    tokenGetter,
  );
}

export async function apiValidateDeployRepoBranch(branch: string, tokenGetter: () => Promise<string>, namespace?: string, _items?: BulkCheckItem[] | ServiceDep[]): Promise<any> {
  let items: any[] = [];
  if (_items && _items?.length > 0) {
    let el: any = _items[0];

    if (el.name && el.branch && el.repoName) {
      items = _items.map((dep) => ({
          appName: dep.name,
          repoName: dep.repoName,
          branch: dep.branch,
        }))
    } else {
      items = _items;
    }
  }

  const params = new URLSearchParams({
    ...(branch ? { branch } : {}),
    ...(namespace ? { namespace } : {}),
  });

  return fetchApi(
    `/api/git/validate-deploy-repo-branch?${params.toString()}`,
    tokenGetter,
    { method: 'POST', ...(items ? { body: JSON.stringify({ items }) } : {}) },
  );
}

export async function apiQuotaCheck(
  team: string,
  namespace: string,
  tokenGetter: () => Promise<string>,
): Promise<QuotaCheckResult> {
  const query = withCluster(
    `/api/quota-check?team=${encodeURIComponent(team)}&namespace=${encodeURIComponent(namespace)}`,
    globalThis.cluster_selected_value,
  );
  const token = await tokenGetter();
  const response = await fetch(query, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const data = (await response.json()) as QuotaCheckResult;
  if (!response.ok) {
    return {
      skipped: false,
      error: data.error || `Error checking quota: ${response.statusText}`,
      message: data.error || data.message,
      runningNamespaces: data.namespaces,
    };
  }

  return data;
}

export async function apiFetchTeams(tokenGetter: () => Promise<string>, allTeams: boolean = false): Promise<string[]> {
  const queryParams = new URLSearchParams();
  if (allTeams) {
    queryParams.set('allTeams', 'true');
  }
  const query = `/api/cortex/teams${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const data = await fetchApi<{ teams: string[] }>(query, tokenGetter);
  return Array.isArray(data.teams) ? data.teams : [];
}

/** The user's own teams; `usingDefaultTeam` is set when they have no Cortex team and a default was assigned. */
export async function apiFetchUserTeams(tokenGetter: () => Promise<string>): Promise<{ teams: string[]; usingDefaultTeam: boolean }> {
  const data = await fetchApi<{ teams: string[]; usingDefaultTeam?: boolean }>('/api/cortex/teams', tokenGetter);
  return { teams: Array.isArray(data.teams) ? data.teams : [], usingDefaultTeam: data.usingDefaultTeam === true };
}

export async function apiCheckJob(id: number, tokenGetter: () => Promise<string>): Promise<{status: 'cancelled' | 'queued' | 'unknown' | 'blocked', id: number, reason: string} | {status: 'triggered', jobUrl: string}> {
  return fetchApi(withCluster(`/api/deployment/deploy-job/${id}`, globalThis.cluster_selected_value), tokenGetter);
}

export async function apiDeployJob(tokenGetter: () => Promise<string>): Promise<{ status: 'triggered', jobUrl: string } | { status: 'queued', id: number}> {
  const reviewData = JSON.parse(localStorage.getItem('reviewData') || '{}')
  if (!reviewData.selectedDependencies) {
    throw new Error('No review data found in localStorage.')
  }
  const deps: Dep[] = reviewData.selectedDependencies

  const meta: Metadata = await apiFetchBusinessData(reviewData.applicationBranch, tokenGetter, reviewData.deployBranch);
  const deploymentList = buildDeploymentGroups(meta, deps)

  const formattedList = deploymentList.map((group: Record<string, any>[]) =>
    group.map((appObj: Record<string, any>) => {
      const key = Object.keys(appObj)[0];
      const value = appObj[key];
      const original = deps.find(d => d.name === key);

      if (original?.deployTag) {
        return { [key]: original.deployTag };
      }

      if (!original) console.warn('App not found in selectedDependencies:', key);
      return { [key]: original?.branch || value.branch || 'master' };
    })
  );

  const payload = { ...reviewData, deploymentList: formattedList };

  return fetchApi(
    withCluster('/api/deployment/deploy-job/v2', globalThis.cluster_selected_value),
    tokenGetter,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function apiFetchBusinessData(featureBranch: string, tokenGetter: () => Promise<string>, medullaBranchName?: string, options?: { raw?: boolean }): Promise<any> {
  const query = new URLSearchParams({ branchName: featureBranch });

  if (medullaBranchName && medullaBranchName !== 'master' && medullaBranchName !== featureBranch) {
    query.set('medullaBranchName', medullaBranchName);
  }

  if (options?.raw) {
    query.set('raw', 'true');
  }

  return fetchApi(`/api/applicationmetadata/files?${query.toString()}`, tokenGetter);
}

export async function usageStatsIncrement(services: string[], selectedBusinessUnits: string[], tokenGetter: () => Promise<string>): Promise<void> {
  if (!services || services.length === 0) {
    console.warn('usageStatsIncrement called with empty or undefined services array.');
    return;
  }

  const body = { services, selectedBusinessUnits }
  const data = await fetchApi(
    '/api/usagestats',
    tokenGetter,
    { method: 'POST', body: JSON.stringify(body) },
  );
  console.log('Usage stats updated on server:', data);
}

export async function apiGetWhitelistExperiences(branch: string, tokenGetter: () => Promise<string>): Promise<string[] | null> {
  try {
    const endpoint = `/api/config/experience-whitelist/${branch}`;
    const accessToken = await tokenGetter();
    const response = await fetch(endpoint, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) throw new Error(`Failed to fetch whitelist for ${branch}`);

    const { experiences } = await response.json();
    return Array.isArray(experiences) ? experiences : null;
  } catch (err) {
    console.warn('Whitelist config fetch error:', err);
    return null;
  }
}

export async function apiCheckAuthorization(tokenGetter: () => Promise<string>): Promise<boolean> {
  const data = await fetchApi<{ enabled: boolean }>(
    withCluster('/api/quickdeploy/authorized', globalThis.cluster_selected_value),
    tokenGetter,
  );
  return data.enabled;
}

export async function apiGetDeployedServices(namespace: string, tokenGetter: () => Promise<string>): Promise<string[]> {
  return fetchApi<string[]>(
    withCluster(`/api/deployed-services?namespace=${encodeURIComponent(namespace)}`, globalThis.cluster_selected_value),
    tokenGetter,
  );
}

export async function apiBudgetInfo(
  team: string,
  tokenGetter: () => Promise<string>,
): Promise<BudgetInfoResult> {
  const queryParams = new URLSearchParams({ team });
  return fetchApi<BudgetInfoResult>(
    withCluster(`/api/budget-info?${queryParams.toString()}`, globalThis.cluster_selected_value),
    tokenGetter,
  );
}

export async function getNamespaceCost(
  namespace: string,
  tokenGetter: () => Promise<string>,
): Promise<NamespaceCost | null> {
  try {
    return await fetchApi<NamespaceCost>(
      withCluster(`/api/kubecost/namespaces/${namespace}`, globalThis.cluster_selected_value),
      tokenGetter,
    );
  } catch (err) {
    console.warn('Error fetching namespace cost:', err);
    return null;
  }
}